import axios from 'axios';
import { API_BASE } from '../config/api.js';

const api = axios.create({
  baseURL: API_BASE,
});

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses — clear token only if one existed (session expired)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error.config?.url || '';
    const isLoginRequest = requestUrl.includes('/login');

    if (error.response?.status === 401 && !isLoginRequest) {
      // Token expired or invalid — clear auth data
      const hadToken = localStorage.getItem('token');
      if (hadToken) {
        if (error.response?.data?.code === 'SESSION_REVOKED') {
          window.dispatchEvent(new CustomEvent('cms:session-revoked', {
            detail: {
              message: error.response?.data?.error ||
                'This session was signed out because the user logged in elsewhere.'
            }
          }));
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Parse a JWT payload without a library.
 * Returns the decoded payload object, or null if the token is invalid.
 */
export function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Check whether the stored JWT is still valid (exists and not expired).
 */
export function isTokenValid() {
  const token = localStorage.getItem('token');
  if (!token) return false;
  const payload = parseJwt(token);
  if (!payload || !payload.exp) return false;
  return Date.now() < payload.exp * 1000;
}

/**
 * Get the role from the JWT token (tamper-proof, unlike localStorage).
 * Returns uppercase role string or ''.
 */
export function getTokenRole() {
  const token = localStorage.getItem('token');
  if (!token) return '';
  const payload = parseJwt(token);
  return String(payload?.role || '').toUpperCase();
}

export default api;
